import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Rocket, CheckCircle2, Target, ShieldCheck, FileText, FileUp, Eye, Trash2, Clock, Receipt, BadgeCheck } from "lucide-react";
import { b2mGetAssessment, b2mSubmitAssessment, b2mPCertList, b2mPCertUpload, b2mPCertDelete, b2mPCertFileBlob, b2mPEviList, b2mPEviUpload, b2mPEviDelete, b2mPEviFileBlob, b2mParticipantReport } from "@/lib/gfApi";

const INK = "#141A32";
const INK2 = "#1E2647";
const GOLD = "#D6A25A";
const TEAL = "#2FA89F";
const RED = "#D9705A";

const CERT_STATUS_COLOR = { "Disahkan": TEAL, "Belum Disahkan": GOLD, "Ditolak": RED };
const EVI_STATUS_COLOR = { "Disahkan": TEAL, "Disemak": "#6B8FD6", "Belum Disemak": GOLD };
const EXPIRY_COLOR = { expired: RED, d30: RED, d60: GOLD, d90: GOLD, ok: TEAL, none: "#8A93B2" };

const barCol = (s) => (s >= 65 ? TEAL : s >= 50 ? GOLD : RED);

function certExpiryText(exp) {
  if (!exp || exp.days_left == null) return "Tiada tarikh luput";
  if (exp.days_left < 0) return `Tamat tempoh ${Math.abs(exp.days_left)} hari lalu`;
  if (exp.days_left === 0) return "Luput hari ini";
  return `${exp.days_left} hari lagi`;
}

function ParticipantReportCard({ pid, token, show }) {
  const [busy, setBusy] = useState(false);
  if (!show || !token) return null;
  const dl = async () => {
    setBusy(true);
    try {
      const blob = await b2mParticipantReport(pid, token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "laporan-kemajuan-brand2market.pdf";
      document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menjana laporan."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 rounded-2xl p-5" style={{ background: "linear-gradient(135deg,#12233A,#1E2647)", border: "1px solid rgba(47,168,159,0.3)" }} data-testid="b2m-p-report-card">
      <p className="flex items-center gap-2 text-sm font-bold text-white"><FileText className="h-4 w-4" style={{ color: GOLD }} /> Laporan Kemajuan Saya</p>
      <p className="mt-1 text-[11px]" style={{ color: "#9AA6C2" }}>Laporan peribadi Brand2Market anda: PRE, 5 peringkat, POST, D30/60/90, pergerakan komersial & langkah seterusnya.</p>
      <button data-testid="b2m-p-report-download" onClick={dl} disabled={busy} className="mt-3 flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Muat Turun Laporan (PDF)
      </button>
    </div>
  );
}


function ParticipantCertVault({ pid, token }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ name: "", cert_type: "Halal", authority: "", issue_date: "", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { b2mPCertList(pid, token).then(setData).catch(() => {}); }, [pid, token]);
  useEffect(() => { load(); }, [load]);
  if (!token) return null;

  const upload = async () => {
    if (!file) { toast.error("Sila pilih fail (PDF/JPG/PNG)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      await b2mPCertUpload(pid, fd, token);
      toast.success("Sijil dimuat naik. Status: Belum Disahkan.");
      setF({ name: "", cert_type: "Halal", authority: "", issue_date: "", expiry_date: "" });
      setFile(null); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal muat naik."); }
    finally { setBusy(false); }
  };
  const view = async (cid) => {
    try { const blob = await b2mPCertFileBlob(pid, cid, token); const url = URL.createObjectURL(blob); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Gagal buka fail."); }
  };
  const del = async (cid) => {
    if (!window.confirm("Padam sijil ini?")) return;
    try { await b2mPCertDelete(pid, cid, token); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal padam."); }
  };

  if (!data) return null;
  const certs = data.certifications || [];
  const types = data.cert_types || [];
  return (
    <div className="mt-8 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="b2m-p-cert-vault">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-bold text-white"><ShieldCheck className="h-4 w-4" style={{ color: GOLD }} /> Peti Pensijilan Saya</p>
        <button data-testid="b2m-p-cert-add-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}>
          <FileUp className="h-3 w-3" /> {open ? "Tutup" : "Muat Naik Sijil"}
        </button>
      </div>
      <p className="mt-1 text-[11px]" style={{ color: "#9AA6C2" }}>Muat naik sijil eksport anda. Setiap sijil akan disemak oleh konsultan sebelum disahkan.</p>

      {open && (
        <div className="mt-3 space-y-2" data-testid="b2m-p-cert-form">
          <input data-testid="b2m-p-cert-name" className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Nama sijil (cth: Sijil Halal JAKIM)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="b2m-p-cert-type" className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.cert_type} onChange={(e) => setF({ ...f, cert_type: e.target.value })}>
              {types.map((t) => <option key={t} value={t} style={{ color: "#000" }}>{t}</option>)}
            </select>
            <input className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Pihak berkuasa" value={f.authority} onChange={(e) => setF({ ...f, authority: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px]" style={{ color: "#9AA6C2" }}>Tarikh keluar</label>
              <input type="date" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.issue_date} onChange={(e) => setF({ ...f, issue_date: e.target.value })} /></div>
            <div><label className="text-[10px]" style={{ color: "#9AA6C2" }}>Tarikh luput</label>
              <input type="date" data-testid="b2m-p-cert-expiry" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} /></div>
          </div>
          <input type="file" data-testid="b2m-p-cert-file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-xs text-white" />
          <p className="text-[10px]" style={{ color: "#6B769A" }}>PDF, JPG atau PNG · maks 10MB.</p>
          <button data-testid="b2m-p-cert-upload" onClick={upload} disabled={busy} className="rounded-full px-4 py-2 text-xs font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>{busy ? "Memuat naik…" : "Simpan Sijil"}</button>
        </div>
      )}

      <div className="mt-3 space-y-2" data-testid="b2m-p-cert-list">
        {certs.length === 0 ? <p className="text-[11px]" style={{ color: "#9AA6C2" }}>Belum ada sijil dimuat naik.</p> : certs.map((c) => (
          <div key={c.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`b2m-p-cert-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-white"><FileText className="h-3.5 w-3.5 shrink-0" style={{ color: GOLD }} /> <span className="truncate">{c.name}</span></p>
                <p className="text-[11px]" style={{ color: "#9AA6C2" }}>{c.cert_type}{c.authority ? ` · ${c.authority}` : ""}</p>
              </div>
              <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${CERT_STATUS_COLOR[c.verification_status]}22`, color: CERT_STATUS_COLOR[c.verification_status] }}>{c.verification_status}</span>
            </div>
            <div className="mt-1.5 flex items-center gap-2 text-[10px]" style={{ color: EXPIRY_COLOR[c.expiry?.bucket] || "#8A93B2" }}>
              <Clock className="h-3 w-3" /> {certExpiryText(c.expiry)}{c.expiry_date ? ` · luput ${c.expiry_date}` : ""}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button data-testid={`b2m-p-cert-view-${c.id}`} onClick={() => view(c.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(255,255,255,0.08)", color: "#C7CEE0" }}><Eye className="h-3 w-3" /> Lihat</button>
              {c.uploaded_by === "participant" && c.verification_status === "Belum Disahkan" && (
                <button data-testid={`b2m-p-cert-del-${c.id}`} onClick={() => del(c.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(217,112,90,0.15)", color: RED }}><Trash2 className="h-3 w-3" /> Padam</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ParticipantEvidenceVault({ pid, token }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ title: "", evidence_type: "Invois", description: "", amount: "", evidence_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { b2mPEviList(pid, token).then(setData).catch(() => {}); }, [pid, token]);
  useEffect(() => { load(); }, [load]);
  if (!token) return null;

  const upload = async () => {
    if (!file) { toast.error("Sila pilih fail (PDF/JPG/PNG)."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
      await b2mPEviUpload(pid, fd, token);
      toast.success("Bukti dimuat naik. Status: Belum Disemak.");
      setF({ title: "", evidence_type: "Invois", description: "", amount: "", evidence_date: "" });
      setFile(null); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal muat naik."); }
    finally { setBusy(false); }
  };
  const view = async (eid) => {
    try { const blob = await b2mPEviFileBlob(pid, eid, token); const url = URL.createObjectURL(blob); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch { toast.error("Gagal buka fail."); }
  };
  const del = async (eid) => {
    if (!window.confirm("Padam bukti ini?")) return;
    try { await b2mPEviDelete(pid, eid, token); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Gagal padam."); }
  };

  if (!data) return null;
  const items = data.evidence || [];
  const types = data.evidence_types || [];
  return (
    <div className="mt-6 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }} data-testid="b2m-p-evidence-vault">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-bold text-white"><Receipt className="h-4 w-4" style={{ color: GOLD }} /> Peti Bukti Komersial Saya</p>
        <button data-testid="b2m-p-evi-add-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold" style={{ background: "rgba(214,162,90,0.15)", color: GOLD }}>
          <FileUp className="h-3 w-3" /> {open ? "Tutup" : "Muat Naik Bukti"}
        </button>
      </div>
      <p className="mt-1 text-[11px]" style={{ color: "#9AA6C2" }}>Muat naik bukti jualan/eksport (invois, pesanan, kontrak). Konsultan akan menyemak setiap bukti.</p>

      {open && (
        <div className="mt-3 space-y-2" data-testid="b2m-p-evi-form">
          <input data-testid="b2m-p-evi-title" className="w-full rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Tajuk (cth: Invois eksport Singapura)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="b2m-p-evi-type" className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.evidence_type} onChange={(e) => setF({ ...f, evidence_type: e.target.value })}>
              {types.map((t) => <option key={t} value={t} style={{ color: "#000" }}>{t}</option>)}
            </select>
            <input className="rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="Nilai (cth: RM12,500)" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
          </div>
          <input type="date" data-testid="b2m-p-evi-date" className="w-full rounded-lg px-2.5 py-1.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} value={f.evidence_date} onChange={(e) => setF({ ...f, evidence_date: e.target.value })} />
          <input type="file" data-testid="b2m-p-evi-file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-xs text-white" />
          <p className="text-[10px]" style={{ color: "#6B769A" }}>PDF, JPG atau PNG · maks 10MB.</p>
          <button data-testid="b2m-p-evi-upload" onClick={upload} disabled={busy} className="rounded-full px-4 py-2 text-xs font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>{busy ? "Memuat naik…" : "Simpan Bukti"}</button>
        </div>
      )}

      <div className="mt-3 space-y-2" data-testid="b2m-p-evi-list">
        {items.length === 0 ? <p className="text-[11px]" style={{ color: "#9AA6C2" }}>Belum ada bukti dimuat naik.</p> : items.map((e) => (
          <div key={e.id} className="rounded-xl p-3" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }} data-testid={`b2m-p-evi-${e.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-white"><Receipt className="h-3.5 w-3.5 shrink-0" style={{ color: GOLD }} /> <span className="truncate">{e.title}</span></p>
                <p className="text-[11px]" style={{ color: "#9AA6C2" }}>{e.evidence_type}{e.amount ? ` · ${e.amount}` : ""}{e.evidence_date ? ` · ${e.evidence_date}` : ""}</p>
              </div>
              <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${EVI_STATUS_COLOR[e.review_status]}22`, color: EVI_STATUS_COLOR[e.review_status] }}>{e.review_status}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button data-testid={`b2m-p-evi-view-${e.id}`} onClick={() => view(e.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(255,255,255,0.08)", color: "#C7CEE0" }}><Eye className="h-3 w-3" /> Lihat</button>
              {e.uploaded_by === "participant" && e.review_status === "Belum Disemak" && (
                <button data-testid={`b2m-p-evi-del-${e.id}`} onClick={() => del(e.id)} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold" style={{ background: "rgba(217,112,90,0.15)", color: RED }}><Trash2 className="h-3 w-3" /> Padam</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function B2MAssessment() {
  const { pid } = useParams();
  const [sp] = useSearchParams();
  const checkpoint = ["post", "day30", "day60", "day90"].includes(sp.get("checkpoint")) ? sp.get("checkpoint") : "pre";
  const token = sp.get("t") || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [answers, setAnswers] = useState({});
  const [impl, setImpl] = useState({});
  const [commercial, setCommercial] = useState({});
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    b2mGetAssessment(pid, checkpoint, token).then((d) => {
      setData(d);
      if (d.already_done) toast.info("Penilaian ini telah dihantar. Anda boleh menghantar semula.");
    }).catch(() => setErr(true));
  }, [pid, checkpoint, token]);

  const isTracking = data?.kind === "tracking";

  const grouped = useMemo(() => {
    if (!data || isTracking) return [];
    return data.stages.map((st) => ({ ...st, items: data.questions.filter((q) => q.stage === st.key) }));
  }, [data, isTracking]);

  const requiredCount = data && !isTracking ? data.questions.filter((q) => !q.na_allowed).length : 0;
  const requiredAnswered = data && !isTracking ? data.questions.filter((q) => !q.na_allowed && answers[q.id] !== undefined && answers[q.id] !== null).length : 0;

  const submit = async () => {
    if (!isTracking && requiredAnswered < requiredCount) { toast.error("Sila jawab semua soalan wajib."); return; }
    setSubmitting(true);
    try {
      const payload = isTracking ? { implementation: impl, commercial, notes } : answers;
      const r = await b2mSubmitAssessment(pid, checkpoint, payload, token);
      setResult(r.result);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghantar. Sila cuba lagi.");
    } finally { setSubmitting(false); }
  };

  if (err) return <Shell><p className="text-center text-sm" style={{ color: "#9AA6C2" }}>Pautan penilaian tidak sah.</p></Shell>;
  if (!data) return <Shell><div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin" style={{ color: GOLD }} /></div></Shell>;

  if (result) {
    const ss = result.stage_scores || {};
    return (
      <Shell>
        <div className="text-center" data-testid="b2m-assess-result">
          <CheckCircle2 className="mx-auto h-10 w-10" style={{ color: TEAL }} />
          <h1 className="mt-3 font-heading text-2xl font-bold text-white">Terima kasih, {data.participant.name.split(" ")[0]}!</h1>
          <p className="mt-1 text-sm" style={{ color: "#9AA6C2" }}>Penilaian {data.checkpoint_label} anda telah direkodkan.</p>
        </div>
        {isTracking ? (
          <div className="mt-6 rounded-2xl p-6 text-center" style={{ background: INK2, border: `1px solid rgba(214,162,90,0.3)` }} data-testid="b2m-day-result">
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GOLD }}>Kadar Pelaksanaan</p>
            <p className="mt-1 font-heading text-5xl font-bold text-white">{result.implementation_rate ?? "—"}<span className="text-xl" style={{ color: "#9AA6C2" }}>%</span></p>
            <p className="mt-2 text-sm" style={{ color: "#9AA6C2" }}>Data komersial anda telah direkod untuk laporan kemajuan.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 rounded-2xl p-6 text-center" style={{ background: INK2, border: `1px solid rgba(214,162,90,0.3)` }}>
              <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GOLD }}>Skor B2M Keseluruhan</p>
              <p className="mt-1 font-heading text-5xl font-bold text-white">{result.overall}<span className="text-xl" style={{ color: "#9AA6C2" }}>/100</span></p>
              {result.status && <p className="mt-1 text-sm font-bold" style={{ color: TEAL }}>{result.status.label}</p>}
              {result.status && <p className="mt-0.5 text-xs" style={{ color: "#9AA6C2" }}>{result.status.desc}</p>}
            </div>
            <div className="mt-5 space-y-2.5">
              {data.stages.map((st) => (
                <div key={st.key} className="flex items-center gap-3" data-testid={`b2m-res-stage-${st.key}`}>
                  <span className="w-28 text-xs font-semibold text-white">{st.label}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.1)" }}>
                    <div className="h-full rounded-full" style={{ width: `${Math.max(5, ss[st.key] || 0)}%`, background: barCol(ss[st.key] || 0) }} />
                  </div>
                  <span className="w-10 text-right text-xs font-bold text-white">{ss[st.key] ?? "—"}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-2xl p-5" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest" style={{ color: GOLD }}><Target className="h-4 w-4" /> Kekangan utama: {result.primary_constraint}</p>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "#C7CEE0" }}>{result.intervention}</p>
            </div>
          </>
        )}
        <p className="mt-6 text-center text-xs" style={{ color: "#6B769A" }}>Konsultan anda akan menghubungi anda dengan pelan intervensi.</p>
        <ParticipantReportCard pid={pid} token={token} show={data?.pre_done} />
        <ParticipantCertVault pid={pid} token={token} />
        <ParticipantEvidenceVault pid={pid} token={token} />
      </Shell>
    );
  }

  if (isTracking) {
    const f = data.form;
    return (
      <Shell>
        <div data-testid="b2m-day-form">
          <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GOLD }}>Brand2Market™ · {data.checkpoint_label}</p>
          <h1 className="mt-1 font-heading text-2xl font-bold text-white">{data.participant.business_name || data.participant.name}</h1>
          <p className="mt-1 text-sm" style={{ color: "#9AA6C2" }}>{f.focus}</p>
        </div>
        <h2 className="mt-6 mb-3 font-heading text-base font-bold text-white">Pelaksanaan</h2>
        <div className="space-y-4">
          {f.implementation.map((q) => (
            <div key={q.id} className="rounded-2xl p-4" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
              <p className="text-sm text-white">{q.text}</p>
              <div className="mt-3 flex gap-2">
                {[1, 2, 3, 4, 5].map((v) => (
                  <button key={v} data-testid={`b2m-impl-${q.id}-${v}`} onClick={() => setImpl({ ...impl, [q.id]: v })}
                    className="h-9 w-9 rounded-full text-sm font-bold" style={impl[q.id] === v ? { background: GOLD, color: INK } : { background: "rgba(255,255,255,0.06)", color: "#C7CEE0", border: "1px solid rgba(255,255,255,0.12)" }}>{v}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <h2 className="mt-7 mb-3 font-heading text-base font-bold text-white">Data Komersial (isi jika ada)</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {f.commercial.map((c) => (
            <div key={c.id} className="rounded-2xl p-3" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
              <label className="text-xs" style={{ color: "#9AA6C2" }}>{c.label}</label>
              <input type="number" data-testid={`b2m-cm-${c.id}`} value={commercial[c.id] ?? ""}
                onChange={(e) => setCommercial({ ...commercial, [c.id]: e.target.value })}
                className="mt-1 w-full rounded-lg px-3 py-2 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} placeholder="—" />
            </div>
          ))}
        </div>
        <textarea data-testid="b2m-day-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Nota / bukti ringkas (pilihan)…" className="mt-4 w-full rounded-xl px-3 py-2.5 text-sm text-white outline-none" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} />
        <div className="sticky bottom-0 mt-8 -mx-6 px-6 py-4" style={{ background: `linear-gradient(to top, ${INK} 70%, transparent)` }}>
          <button data-testid="b2m-assess-submit" onClick={submit} disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-full py-4 text-base font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Hantar {data.checkpoint_label}
          </button>
        </div>
        <ParticipantReportCard pid={pid} token={token} show={data?.pre_done} />
        <ParticipantCertVault pid={pid} token={token} />
        <ParticipantEvidenceVault pid={pid} token={token} />
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="b2m-assess-form">
        <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: GOLD }}>Brand2Market™ · Penilaian {checkpoint.toUpperCase()}</p>
        <h1 className="mt-1 font-heading text-2xl font-bold text-white">{data.participant.business_name || data.participant.name}</h1>
        <p className="mt-1 text-sm" style={{ color: "#9AA6C2" }}>Nilai kedudukan semasa anda dengan jujur. Skala 1 (lemah) hingga 5 (sangat baik).</p>
      </div>
      {grouped.map((st, si) => (
        <div key={st.key} className="mt-7" data-testid={`b2m-stage-${st.key}`}>
          <div className="mb-3 flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold" style={{ background: GOLD, color: INK }}>{si + 1}</span>
            <h2 className="font-heading text-base font-bold text-white">{st.label}</h2>
          </div>
          <div className="space-y-4">
            {st.items.map((q) => (
              <div key={q.id} className="rounded-2xl p-4" style={{ background: INK2, border: "1px solid rgba(255,255,255,0.08)" }}>
                <p className="text-sm leading-relaxed text-white">{q.text}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button key={v} data-testid={`b2m-q-${q.id}-${v}`}
                      onClick={() => setAnswers({ ...answers, [q.id]: v })}
                      className="h-9 w-9 rounded-full text-sm font-bold transition-transform active:scale-95"
                      style={answers[q.id] === v
                        ? { background: GOLD, color: INK }
                        : { background: "rgba(255,255,255,0.06)", color: "#C7CEE0", border: "1px solid rgba(255,255,255,0.12)" }}>
                      {v}
                    </button>
                  ))}
                  {q.na_allowed && (
                    <button data-testid={`b2m-q-${q.id}-na`} onClick={() => setAnswers({ ...answers, [q.id]: null })}
                      className="h-9 rounded-full px-3 text-xs font-bold transition-transform active:scale-95"
                      style={answers[q.id] === null
                        ? { background: TEAL, color: INK }
                        : { background: "rgba(255,255,255,0.06)", color: "#9AA6C2", border: "1px solid rgba(255,255,255,0.12)" }}>
                      Tidak berkaitan
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="sticky bottom-0 mt-8 -mx-6 px-6 py-4" style={{ background: `linear-gradient(to top, ${INK} 70%, transparent)` }}>
        <button data-testid="b2m-assess-submit" onClick={submit} disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-full py-4 text-base font-bold disabled:opacity-60" style={{ background: GOLD, color: INK }}>
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
          Hantar Penilaian ({requiredAnswered}/{requiredCount})
        </button>
      </div>
      <ParticipantReportCard pid={pid} token={token} show={data?.pre_done} />
      <ParticipantCertVault pid={pid} token={token} />
      <ParticipantEvidenceVault pid={pid} token={token} />
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen font-body" style={{ background: INK }} data-testid="b2m-assessment-page">
      <div className="mx-auto max-w-xl px-6 py-8">
        <div className="mb-2 flex items-center gap-2">
          <Rocket className="h-5 w-5" style={{ color: GOLD }} />
          <span className="font-heading text-sm font-bold text-white">Brand2Market™</span>
        </div>
        {children}
      </div>
    </div>
  );
}
